import AddIcon from "@mui/icons-material/Add";
import { Button, Menu, MenuItem, Stack } from "@mui/material";
import { MouseEvent, memo, useCallback, useState } from "react";
import { AddableDocumentCandidate } from "../../utils/addableDocumentCandidates";

export type AddDocumentControlProps = {
  candidates: AddableDocumentCandidate[];
  onAddDocument: (candidate: AddableDocumentCandidate) => void;
};

export const AddDocumentControl = memo((props: AddDocumentControlProps) => {
  const { candidates, onAddDocument } = props;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleAdd = useCallback(
    (candidate: AddableDocumentCandidate) => {
      setAnchorEl(null);
      onAddDocument(candidate);
    },
    [onAddDocument],
  );

  const handleButtonClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (candidates.length === 1) {
        handleAdd(candidates[0]);
        return;
      }

      setAnchorEl(event.currentTarget);
    },
    [candidates, handleAdd],
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  if (candidates.length === 0) {
    return null;
  }

  const buttonLabel =
    candidates.length === 1
      ? `Add ${candidates[0].label}`
      : "Add document";

  return (
    <Stack alignItems="flex-start" spacing={1.5} sx={{ margin: "12px 0" }}>
      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleButtonClick}
        aria-haspopup={candidates.length > 1 ? "menu" : undefined}
      >
        {buttonLabel}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        {candidates.map((candidate) => (
          <MenuItem
            key={candidate.documentType}
            onClick={() => handleAdd(candidate)}
          >
            {candidate.label}
          </MenuItem>
        ))}
      </Menu>
    </Stack>
  );
});
